import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SupplierForm = ({ supplier, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    farmerId: '',
    location: '',
    region: '',
    phone: '',
    email: '',
    farmSize: '',
    cattleCount: '',
    dailyVolume: '',
    qualityRating: '',
    paymentPreference: '',
    bankAccount: '',
    supplyFluctuation: '',
    rejectionRate: '',
    contractDuration: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier?.name || '',
        farmerId: supplier?.farmerId || '',
        location: supplier?.location || '',
        region: supplier?.region || '',
        phone: supplier?.phone || '',
        email: supplier?.email || '',
        farmSize: supplier?.farmSize || '',
        cattleCount: supplier?.cattleCount || '',
        dailyVolume: supplier?.dailyVolume || '',
        qualityRating: supplier?.qualityRating || '',
        paymentPreference: supplier?.paymentPreference || '',
        bankAccount: supplier?.bankAccount || '',
        supplyFluctuation: supplier?.supplyFluctuation || '',
        rejectionRate: supplier?.rejectionRate || '',
        contractDuration: supplier?.contractDuration || '',
        notes: supplier?.notes || '',
      });
    }
  }, [supplier]);

  const regionOptions = [
    { value: 'jawa-barat', label: 'Jawa Barat' },
    { value: 'jawa-tengah', label: 'Jawa Tengah' },
    { value: 'jawa-timur', label: 'Jawa Timur' },
    { value: 'banten', label: 'Banten' },
  ];

  const paymentOptions = [
    { value: 'weekly', label: 'Mingguan' },
    { value: 'biweekly', label: 'Dua Mingguan' },
    { value: 'monthly', label: 'Bulanan' },
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Nama peternak wajib diisi';
    if (!formData?.location?.trim()) newErrors.location = 'Lokasi wajib diisi';
    if (!formData?.region) newErrors.region = 'Region wajib dipilih';
    if (!formData?.phone?.trim()) newErrors.phone = 'Nomor telepon wajib diisi';
    if (!formData?.dailyVolume) newErrors.dailyVolume = 'Volume harian wajib diisi';
    if (!formData?.cattleCount) newErrors.cattleCount = 'Jumlah ternak wajib diisi';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {supplier ? 'Edit Data Peternak' : 'Tambah Peternak Baru'}
            </h3>
            <p className="text-sm text-muted-foreground">
              Lengkapi informasi peternak dan data produksi
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="UserCircle" size={18} color="var(--color-primary)" />
            Informasi Dasar
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nama Peternak"
              type="text"
              placeholder="Masukkan nama lengkap"
              value={formData?.name}
              onChange={(e) => handleChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />
            <Input
              label="ID Peternak"
              type="text"
              placeholder="Otomatis jika kosong"
              value={formData?.farmerId}
              onChange={(e) => handleChange('farmerId', e?.target?.value)}
            />
            <Input
              label="Lokasi Peternakan"
              type="text"
              placeholder="Alamat lengkap"
              value={formData?.location}
              onChange={(e) => handleChange('location', e?.target?.value)}
              error={errors?.location}
              required
            />
            <Select
              label="Region"
              options={regionOptions}
              value={formData?.region}
              onChange={(value) => handleChange('region', value)}
              error={errors?.region}
              required
              placeholder="Pilih region"
            />
            <Input
              label="Nomor Telepon"
              type="tel"
              placeholder="08xxxxxxxxxx"
              value={formData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />
            <Input
              label="Email"
              type="email"
              placeholder="email@example.com"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
            />
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Home" size={18} color="var(--color-primary)" />
            Data Peternakan
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Luas Lahan (Ha)"
              type="number"
              placeholder="0.0"
              value={formData?.farmSize}
              onChange={(e) => handleChange('farmSize', e?.target?.value)}
            />
            <Input
              label="Jumlah Ternak"
              type="number"
              placeholder="0"
              value={formData?.cattleCount}
              onChange={(e) => handleChange('cattleCount', e?.target?.value)}
              error={errors?.cattleCount}
              required
            />
            <Input
              label="Volume Harian (Liter)"
              type="number"
              placeholder="0"
              value={formData?.dailyVolume}
              onChange={(e) => handleChange('dailyVolume', e?.target?.value)}
              error={errors?.dailyVolume}
              required
            />
            <Input
              label="Rating Kualitas (%)"
              type="number"
              placeholder="0-100"
              value={formData?.qualityRating}
              onChange={(e) => handleChange('qualityRating', e?.target?.value)}
              min="0"
              max="100"
            />
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="DollarSign" size={18} color="var(--color-primary)" />
            Informasi Pembayaran
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Preferensi Pembayaran"
              options={paymentOptions}
              value={formData?.paymentPreference}
              onChange={(value) => handleChange('paymentPreference', value)}
              placeholder="Pilih periode pembayaran"
            />
            <Input
              label="Nomor Rekening"
              type="text"
              placeholder="Nomor rekening bank"
              value={formData?.bankAccount}
              onChange={(e) => handleChange('bankAccount', e?.target?.value)}
            />
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
            Analisis Pasokan
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Fluktuasi Pasokan (%)"
              type="number"
              placeholder="0-100"
              value={formData?.supplyFluctuation}
              onChange={(e) => handleChange('supplyFluctuation', e?.target?.value)}
              description="Persentase variasi volume harian"
              min="0"
              max="100"
            />
            <Input
              label="Tingkat Penolakan (%)"
              type="number"
              placeholder="0-100"
              value={formData?.rejectionRate}
              onChange={(e) => handleChange('rejectionRate', e?.target?.value)}
              description="Persentase susu yang ditolak"
              min="0"
              max="100"
            />
            <Input
              label="Durasi Kontrak (Bulan)"
              type="number"
              placeholder="0"
              value={formData?.contractDuration}
              onChange={(e) => handleChange('contractDuration', e?.target?.value)}
            />
          </div>
        </div>

        <div>
          <Input
            label="Catatan Tambahan"
            type="text"
            placeholder="Informasi tambahan tentang peternak"
            value={formData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 pt-4 border-t border-border">
        <Button
          type="submit"
          variant="default"
          iconName="Save"
          iconPosition="left"
        >
          {supplier ? 'Update Data' : 'Simpan Data'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          iconName="X"
          iconPosition="left"
        >
          Batal
        </Button>
      </div>
    </form>
  );
};

export default SupplierForm;